import React, { useRef, useState } from 'react'
import { isEmpty } from 'lodash'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { Thumbnail } from 'components'
import ModalPortal from 'components/Modal/ModalPortal'
import { IconClose, IconArrowDown } from 'components/Icons'
import { useOnClickOutside } from 'helpers/hooks'

import clsx from 'clsx'

export interface PreviewImageProps {
  list: { src: string | undefined }[]
  index?: number
  className?: string
}

export const PreviewImage: React.FC<PreviewImageProps> = ({
  list,
  className,
}: PreviewImageProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)

  const divRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation('common')
  const history = useHistory()

  const { pathname, hash } = history.location

  const base = 'preview-image'
  const imageLength = `${list.length} ${t('images.label')}`
  const isDisplayNumberImages = Boolean(!isEmpty(list) && list.length > 1)

  const handleOpen = () => {
    setIsOpen(true)
    history.push('#carousel__slide1')
  }

  const handleClose = () => {
    setIsOpen(false)
    history.replace(pathname, '')
  }

  useOnClickOutside(divRef, handleClose)

  const renderModal = () => {
    let currentOrder = 0

    const renderLeftArrow = (order: number) => {
      return (
        order > 0 && (
          <a
            data-testid='modal-arrow-left-icon'
            className={`${base}__modal-arrow-left-icon ${base}__model-bg-arrow-icon`}
            href={`#carousel__slide${order}`}
          >
            <IconArrowDown
              className={`icon--white ${base}__rotate-arrow-left`}
              variants='sm'
            />
          </a>
        )
      )
    }

    const renderRightArrow = (order: number) => {
      return (
        order <= list.length && (
          <a
            data-testid='modal-arrow-right-icon'
            className={`${base}__modal-arrow-right-icon ${base}__model-bg-arrow-icon`}
            href={`#carousel__slide${order}`}
          >
            <IconArrowDown
              className={`icon--white ${base}__rotate-arrow-right`}
              variants='sm'
            />
          </a>
        )
      )
    }

    if (hash.search('#carousel__slide') >= 0) {
      currentOrder = parseInt(hash.toString().replace('#carousel__slide', ''))
    }

    return (
      <div
        className={`${base}__modal`}
        data-testid='preview-images-slide-modal'
      >
        <div className={`${base}__modal-content carousel`} ref={divRef}>
          <label className={`${base}__modal-title`}>{`${t(
            'imageSequence.label',
          )} ${currentOrder}/${list.length}`}</label>

          {renderLeftArrow(currentOrder - 1)}
          {renderRightArrow(currentOrder + 1)}

          <ol className='carousel__viewport'>
            {list.map((item, index) => {
              const order = index + 1

              return (
                <li
                  id={`carousel__slide${order}`}
                  tabIndex={index}
                  className='carousel__slide'
                  key={`carousel__slide${order}`}
                >
                  <div className='carousel__snapper'>
                    <div className={`${base}__modal-content-image`}>
                      <div className={`${base}__modal-content-image-source`}>
                        <Thumbnail
                          className={clsx(`${base}__modal-thumbnail-source`)}
                          src={item.src}
                        />
                        <div
                          data-testid='preview-images-slide-modal-close-icon'
                          className={`${base}__modal-close-icon`}
                          onClick={handleClose}
                        >
                          <IconClose
                            className={`icon--white ${base}__modal-close-icon-size`}
                            variants='xs'
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              )
            })}
          </ol>
        </div>
      </div>
    )
  }

  const renderMultipleImage = (
    list: { src: string | undefined }[],
  ): JSX.Element => {
    return (
      <div
        data-testid='thumbnail-source-multiple'
        className={clsx(`${base}__thumbnail-layout`, className)}
        onClick={handleOpen}
      >
        <div className={`${base}__thumbnail-layout-opacity`} />
        <label className={`${base}__number-images`}>{imageLength}</label>
        <Thumbnail className={`${base}__thumbnail-source`} src={list[0].src} />
      </div>
    )
  }

  const renderSingleImage = (
    list: { src: string | undefined }[],
  ): JSX.Element => {
    return (
      <div
        data-testid='thumbnail-source-single'
        className={clsx(`${base}__thumbnail-layout`, className)}
      >
        <Thumbnail
          className={`${base}__thumbnail-source`}
          src={list[0].src}
          onClick={handleOpen}
        />
      </div>
    )
  }

  const renderDefaultThumbnail = (
    <Thumbnail
      className={clsx(`${base}__thumbnail-layout`, className)}
      src={undefined}
    />
  )

  const renderImage = !isEmpty(list)
    ? renderSingleImage(list)
    : renderDefaultThumbnail

  return (
    <div className={base}>
      {isDisplayNumberImages ? renderMultipleImage(list) : renderImage}
      {isOpen && divRef ? <ModalPortal>{renderModal()}</ModalPortal> : null}
    </div>
  )
}

export default PreviewImage
